import React, { useEffect, useId, useRef, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthContext';
import { projectMemoryService, type ProjectMemoryJob, type ProjectMemoryJobMode } from '@/services/projectMemoryService';
import './ProjectMemoryPanel.css';

type Props = { projectId: number; projectNumber?: string };
type Attempt = { mode: ProjectMemoryJobMode; key: string; resumeJobId?: string; previousJobId?: string };
const active = (job: ProjectMemoryJob | null) => job?.status === 'queued' || job?.status === 'running';
const newKey = () => `project-memory:${crypto.randomUUID()}`;
const accessDenied = (error: unknown) => [401, 403].includes(Number((error as { status?: number })?.status));
const accessChanged = (error: unknown) => (error as { code?: string })?.code === 'memory_access_changed';
const initializationIncomplete = (error: unknown) => (error as { code?: string })?.code === 'history_start_incomplete';
const knownJobConflict = (error: unknown) => ['history_job_active', 'memory_job_superseded'].includes((error as { code?: string })?.code || '');
const sourceNames: Record<string, string> = { quotations: 'Quotations', tasks: 'Tasks', certificates: 'Certificates', 'certificate-versions': 'Certificate versions', documents: 'Project documents', 'email-trail': 'Email trail', 'email-attachments': 'Email attachments', 'email-outcomes': 'Email outcomes' };
const primaryDomains = ['email-trail', 'tasks', 'documents', 'quotations', 'certificates'];
type MemorySource = NonNullable<ProjectMemoryJob['sources']>[number];
const sourceState = (source: MemorySource) => source.status === 'restricted' ? 'Restricted' : source.status === 'unavailable' ? 'Unavailable' : source.status === 'pending' ? 'Not started' : source.complete ? 'Complete' : 'In progress';
const sourceCount = (source: MemorySource) => source.status !== 'current' ? sourceState(source)
  : `${source.records.toLocaleString()}${source.domain === 'documents' ? ' cached' : ''}${source.complete ? '' : ' so far'}`;

function safeError(error: unknown, action = false): string {
  const failure = error as { status?: number; code?: string } | null;
  if (failure?.code === 'project_memory_disabled') return 'Project memory is not enabled on this deployment yet.';
  if (accessChanged(failure)) return 'Your access has changed. The previous job is hidden; start a fresh refresh or backfill with your current access.';
  if (initializationIncomplete(failure)) return 'Task initialization was interrupted. Choose the same action to recover initialization, or use Retry same request if shown.';
  if (failure?.status === 401) return 'Your sign-in session could not be verified. Sign in again, then check status.';
  if (failure?.status === 403) return 'You are signed in, but Project memory could not verify your project permissions. Ask an administrator to check your account mapping and project access, then check status.';
  return action
    ? 'The request outcome could not be confirmed. Check status, or retry the same request safely.'
    : 'Could not check the latest status. Any queued work continues in the background; check again shortly.';
}

/** Keyed by signed-in identity and project so old responses never cross scopes. */
export function ProjectMemoryPanel(props: Props) {
  const { user, isAuthenticated } = useAuth();
  const identity = `${user?.userId || ''}:${user?.userDetails?.trim().toLowerCase() || ''}`;
  if (!isAuthenticated || !user?.userId) return null;
  return <ProjectMemoryPanelContent key={`${props.projectId}:${identity}`} {...props} />;
}

function ProjectMemoryPanelContent({ projectId, projectNumber }: Props) {
  const titleId = useId();
  const retryHintId = useId();
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const attempt = useRef<Attempt | null>(null);
  const [job, setJob] = useState<ProjectMemoryJob | null>(null);
  const [busy, setBusy] = useState<'loading' | 'request' | null>('loading');
  const [error, setError] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [statusKnown, setStatusKnown] = useState(false);
  const [initializationInterrupted, setInitializationInterrupted] = useState(false);

  const apply = (result: ProjectMemoryJob | null, version: number) => {
    if (!mounted.current || requestVersion.current !== version) return false;
    setJob(result); setError(null); setStatusKnown(true); setInitializationInterrupted(false);
    const expected = attempt.current;
    const matchesAttempt = !expected || (result?.mode === expected.mode && (expected.resumeJobId
      ? result.jobId === expected.resumeJobId : result.jobId !== expected.previousJobId));
    if (result && matchesAttempt && (active(result) || result.status === 'completed')) {
      attempt.current = null; setUncertain(false);
    }
    return true;
  };

  const checkStatus = async (currentJob?: ProjectMemoryJob) => {
    const version = ++requestVersion.current;
    setBusy('loading');
    try {
      apply(currentJob ? await projectMemoryService.getJob(projectId, currentJob.jobId) : await projectMemoryService.getLatest(projectId), version);
    } catch (failure) {
      if (mounted.current && requestVersion.current === version) {
        setError(safeError(failure));
        if (accessDenied(failure)) { setJob(null); setStatusKnown(false); setInitializationInterrupted(false); }
        if (accessChanged(failure)) { setJob(null); setStatusKnown(true); setInitializationInterrupted(false); attempt.current = null; setUncertain(false); }
        // The backend has a durable seed but no completed start receipt. A new
        // request can recover it after remount; an existing uncertain attempt
        // must retain its original key and use the retry control instead.
        if (initializationIncomplete(failure)) { setJob(null); setStatusKnown(true); setInitializationInterrupted(true); }
      }
    } finally {
      if (mounted.current && requestVersion.current === version) setBusy(null);
    }
  };

  useEffect(() => {
    mounted.current = true;
    void checkStatus(); // Read-only restoration; opening the panel never creates a job.
    return () => { mounted.current = false; requestVersion.current++; };
    // Identity and project changes remount the inner panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!active(job) || busy) return;
    const timeout = window.setTimeout(() => { void checkStatus(job!); }, 5000);
    return () => window.clearTimeout(timeout);
    // Each settled status request schedules the next read; no overlapping polls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, busy]);

  const run = async (mode: ProjectMemoryJobMode, resumeJobId?: string) => {
    if (busy || (active(job) && !resumeJobId)) return;
    if (!attempt.current) attempt.current = { mode, resumeJobId, previousJobId: job?.jobId, key: newKey() };
    const current = attempt.current;
    const version = ++requestVersion.current;
    setBusy('request'); setError(null);
    try {
      const result = current.resumeJobId
        ? await projectMemoryService.resume(projectId, current.resumeJobId, current.key)
        : await projectMemoryService.start(projectId, current.mode, current.key);
      if (apply(result, version)) { attempt.current = null; setUncertain(false); }
    } catch (failure) {
      if (mounted.current && requestVersion.current === version) {
        if (knownJobConflict(failure)) {
          // A definite no-op should not trap an incorrect-mode retry. Restore
          // the existing job with a read, never automatically submit another.
          attempt.current = null; setUncertain(false);
          await checkStatus();
          return;
        }
        setError(safeError(failure, true));
        if (accessChanged(failure)) { setJob(null); setStatusKnown(true); setInitializationInterrupted(false); attempt.current = null; setUncertain(false); }
        else setUncertain(true);
      }
    } finally {
      if (mounted.current && requestVersion.current === version) setBusy(null);
    }
  };

  const pending = active(job);
  const disabled = Boolean(busy || pending || uncertain || !statusKnown);
  const statusLabel = job ? { queued: 'Queued', running: 'Updating memory', failed: 'Needs attention', completed: job.coverage === 'complete' ? 'Memory saved' : 'Saved · partial coverage' }[job.status] : initializationInterrupted ? 'Initialization interrupted' : statusKnown ? 'No previous job' : 'Status unavailable';
  const counts = job?.progress;
  const primarySources = primaryDomains.flatMap(domain => job?.sources?.filter(source => source.domain === domain) || []);
  const otherIssues = job?.sources?.filter(source => !primaryDomains.includes(source.domain) && ['restricted', 'unavailable'].includes(source.status)) || [];
  const sourceChanged = job?.errorCode && ['memory_access_changed', 'memory_history_access_changed', 'memory_history_source_changed'].includes(job.errorCode);

  return <section className="project-memory-panel" aria-labelledby={titleId}>
    <header className="project-memory-panel__header">
      <div className="project-memory-panel__title">
        <h3 id={titleId}>Project memory</h3>
        <span className={`project-memory-panel__state project-memory-panel__state--${job?.status === 'completed' && job.coverage !== 'complete' ? 'partial' : job?.status || 'empty'}`} aria-live="polite" aria-atomic="true">{busy === 'request' ? 'Submitting request…' : busy === 'loading' && !statusKnown ? 'Checking status…' : statusLabel}</span>
      </div>
      <div className="project-memory-panel__actions">
        <button type="button" className="project-memory-panel__button project-memory-panel__button--primary" aria-label="Refresh memory" title="Refresh the current memory preview" disabled={disabled} onClick={() => void run('refresh')}>Refresh</button>
        <button type="button" className="project-memory-panel__button" aria-label="Backfill project history" title="Save older linked records into private history" disabled={disabled} onClick={() => void run('backfill')}>Backfill history</button>
        <button type="button" className="project-memory-panel__button project-memory-panel__button--icon" aria-label="Check status" title="Check status" disabled={Boolean(busy)} onClick={() => void checkStatus()}><RefreshCw size={16} aria-hidden="true" /></button>
      </div>
    </header>
    {job ? <>
      <div className="project-memory-panel__summary">
        {job.mode === 'backfill' && counts ? <p><strong>{counts.recordsRead.toLocaleString()}</strong> {counts.recordsRead === 1 ? 'record' : 'records'} processed <span aria-hidden="true">·</span> <strong>{counts.domainsCompleted}/{counts.domainsTotal}</strong> sources checked</p>
          : <p>{job.status === 'completed' ? 'Current preview refreshed. Backfill includes older linked records.' : job.status === 'failed' ? 'Preview refresh paused.' : job.status === 'queued' ? 'Preview refresh queued.' : 'Refreshing the current preview.'}</p>}
        <p className="project-memory-panel__updated" title={job.updatedAt.label}>Updated {job.updatedAt.value}</p>
      </div>
      {primarySources.length ? <dl className="project-memory-panel__sources-preview" aria-label="Source summary">{primarySources.map(source => <div key={source.domain}>
        <dt>{sourceNames[source.domain]}</dt><dd className={`project-memory-panel__source-state--${source.status}`}>{sourceCount(source)}</dd>
      </div>)}</dl> : null}
      {otherIssues.length ? <p className="project-memory-panel__warning">{otherIssues.map(source => `${sourceNames[source.domain] || source.domain}: ${sourceState(source)}`).join(' · ')}</p> : null}
      {pending ? <p className="project-memory-panel__current">{job?.status === 'running' && counts?.currentDomain ? `Processing ${sourceNames[counts.currentDomain] || counts.currentDomain}. ` : ''}Continues in background.</p> : null}
      {job.status === 'failed' && !error ? <p className="project-memory-panel__warning">{sourceChanged ? 'Access or source changed. Saved progress is retained.' : 'Processing paused. Saved progress is retained.'}</p> : null}
    </> : statusKnown && !error ? <p className="project-memory-panel__empty">Refresh a quick preview or backfill older linked records.</p> : null}
    {error ? <p className="project-memory-panel__error" role="alert">{error}</p> : null}
    {(uncertain && attempt.current) || (job?.status === 'failed' && !uncertain) ? <div className="project-memory-panel__recovery">
      {uncertain && attempt.current ? <button type="button" className="project-memory-panel__button" disabled={Boolean(busy)} onClick={() => void run(attempt.current!.mode, attempt.current!.resumeJobId)}>Retry same request</button> : null}
      {job?.status === 'failed' && !uncertain ? <button type="button" className="project-memory-panel__button project-memory-panel__button--primary" disabled={Boolean(busy)} onClick={() => void run(job.mode, job.jobId)}>Resume {job.mode === 'backfill' ? 'backfill' : 'refresh'}</button> : null}
    </div> : null}
    <details className="project-memory-panel__details">
      <summary>Details</summary>
      <div className="project-memory-panel__detail-content">
        {projectNumber ? <p>Project {projectNumber}</p> : null}
        {job ? <>
          <p>{job.message}</p>
          {counts && job.mode === 'backfill' ? <p>{counts.pagesRead.toLocaleString()} {counts.pagesRead === 1 ? 'page' : 'pages'} processed · Source coverage for this run</p> : null}
          {job.sources?.length ? <dl className="project-memory-panel__source-details">{job.sources.map(source => <div key={source.domain}>
            <dt>{sourceNames[source.domain] || source.domain}</dt>
            <dd><span className={`project-memory-panel__source-state--${source.status}`}>{sourceState(source)}</span><span>{source.records.toLocaleString()} {source.records === 1 ? 'record' : 'records'} · {source.pages.toLocaleString()} {source.pages === 1 ? 'page' : 'pages'}</span></dd>
          </div>)}</dl> : null}
          <p>Updated {job.updatedAt.value} · {job.updatedAt.label}</p>
          {job.status === 'completed' ? <p>{job.mode === 'refresh' ? 'Current bounded preview saved. Use Backfill project history for older linked records.' : job.coverage === 'complete' ? 'Completed for available linked database records at this run’s cutoff.' : 'Some sources were unavailable or restricted. This is not a complete project history.'} Unimported or deleted email history is not included.</p> : null}
        </> : <p>Refresh updates the current preview. Backfill saves older linked database records. Neither action changes project work.</p>}
        {pending && !uncertain ? <div className="project-memory-panel__retry">
          <p id={retryHintId}>If progress has stopped, retry from the saved checkpoint.</p>
          <button type="button" className="project-memory-panel__button" disabled={Boolean(busy)} aria-describedby={retryHintId} onClick={() => void run(job!.mode, job!.jobId)}>Retry background processing</button>
        </div> : null}
        <p className="project-memory-panel__privacy"><ShieldCheck size={15} aria-hidden="true" />Private memory; only index.json shared. No mailbox import.</p>
      </div>
    </details>
  </section>;
}
